/**
 * Makes a dialog window movable from an explicit header/grip.
 *
 * Dialogs normally begin as grid-centred, transformed children. On the first
 * drag this action pins the exact painted rect as a fixed-position window, so
 * movement never fights a Svelte scale transition or a Tailwind centring
 * transform. The window remains in the viewport at all times.
 */
export interface DraggableDialogOptions {
  /** Keep a small, reliable grab area inside the viewport. */
  gutter?: number;
}

const INTERACTIVE = "button, a, input, select, textarea, summary, [role='button'], [contenteditable='true'], [data-dialog-no-drag]";

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Add `use:draggableDialog` to the window and `data-dialog-drag-handle` to a
 * non-interactive part of its header. Buttons and form controls always retain
 * their native pointer behaviour.
 */
export function draggableDialog(node: HTMLElement, initial: DraggableDialogOptions = {}) {
  let options = initial;
  let activePointer: number | null = null;
  let startX = 0;
  let startY = 0;
  let startLeft = 0;
  let startTop = 0;
  let width = 0;
  let height = 0;
  let pinned = false;

  function keepPinnedDialogVisible(): void {
    if (!pinned) return;
    const rect = node.getBoundingClientRect();
    const gutter = options.gutter ?? 12;
    const left = clamp(rect.left, gutter, Math.max(gutter, window.innerWidth - rect.width - gutter));
    const top = clamp(rect.top, gutter, Math.max(gutter, window.innerHeight - rect.height - gutter));
    if (Math.round(left) !== Math.round(rect.left)) node.style.left = `${Math.round(left)}px`;
    if (Math.round(top) !== Math.round(rect.top)) node.style.top = `${Math.round(top)}px`;
  }

  function finish(pointerId?: number): void {
    if (activePointer === null || (pointerId !== undefined && pointerId !== activePointer)) return;
    if (node.hasPointerCapture(activePointer)) node.releasePointerCapture(activePointer);
    activePointer = null;
    node.classList.remove("dialog-is-dragging");
  }

  function onPointerDown(event: PointerEvent): void {
    if (!event.isPrimary || event.button !== 0) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target || target.closest(INTERACTIVE)) return;
    const handle = target.closest<HTMLElement>("[data-dialog-drag-handle]");
    if (!handle || !node.contains(handle)) return;

    event.preventDefault();
    const rect = node.getBoundingClientRect();
    // Preserve the painted position before taking the panel out of the
    // centring grid. This also neutralises `-translate-1/2` pile windows.
    node.style.position = "fixed";
    node.style.left = `${Math.round(rect.left)}px`;
    node.style.top = `${Math.round(rect.top)}px`;
    node.style.right = "auto";
    node.style.bottom = "auto";
    node.style.translate = "none";
    node.style.transform = "none";
    pinned = true;
    activePointer = event.pointerId;
    startX = event.clientX;
    startY = event.clientY;
    startLeft = rect.left;
    startTop = rect.top;
    width = rect.width;
    height = rect.height;
    node.setPointerCapture(event.pointerId);
    node.classList.add("dialog-is-dragging");
  }

  function onPointerMove(event: PointerEvent): void {
    if (event.pointerId !== activePointer) return;
    const gutter = options.gutter ?? 12;
    const maxLeft = Math.max(gutter, window.innerWidth - width - gutter);
    const maxTop = Math.max(gutter, window.innerHeight - height - gutter);
    const nextLeft = clamp(startLeft + event.clientX - startX, gutter, maxLeft);
    const nextTop = clamp(startTop + event.clientY - startY, gutter, maxTop);
    node.style.left = `${Math.round(nextLeft)}px`;
    node.style.top = `${Math.round(nextTop)}px`;
  }

  function onPointerEnd(event: PointerEvent): void {
    finish(event.pointerId);
  }

  node.addEventListener("pointerdown", onPointerDown);
  node.addEventListener("pointermove", onPointerMove);
  node.addEventListener("pointerup", onPointerEnd);
  node.addEventListener("pointercancel", onPointerEnd);
  node.addEventListener("lostpointercapture", onPointerEnd);
  window.addEventListener("resize", keepPinnedDialogVisible);
  window.visualViewport?.addEventListener("resize", keepPinnedDialogVisible);

  return {
    update(next: DraggableDialogOptions = {}) {
      options = next;
    },
    destroy() {
      finish();
      node.removeEventListener("pointerdown", onPointerDown);
      node.removeEventListener("pointermove", onPointerMove);
      node.removeEventListener("pointerup", onPointerEnd);
      node.removeEventListener("pointercancel", onPointerEnd);
      node.removeEventListener("lostpointercapture", onPointerEnd);
      window.removeEventListener("resize", keepPinnedDialogVisible);
      window.visualViewport?.removeEventListener("resize", keepPinnedDialogVisible);
    },
  };
}
